import {
    Link,
    Outlet,
    createFileRoute,
    useNavigate,
    useRouterState,
} from '@tanstack/react-router'
import { LogOut, Menu, QrCode, X, Zap } from 'lucide-react'
import { useState } from 'react'

import { AppSidebar } from '@/components/sidebar/app-sidebar'
import { getNavItems } from '@/components/sidebar/nav-items'
import { filterNavEntries } from '@/components/sidebar/nav-filter'
import { buildSidebar } from '@/components/sidebar/nav-layout'
import { useDashboardRole } from '@/components/dashboard/use-dashboard-role'
import { ActiveTripBadge } from '@/components/delivery/active-trip-badge'
import { NotificationDropdown } from '@/components/notifications/notification-dropdown'
import { WhatsNewTrigger } from '@/components/changelog/whats-new-trigger'
import { QRScannerDialog } from '@/components/sections/qr-scanner-dialog'
import { SupportReportTrigger } from '@/components/support/support-report-trigger'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useSidebarLayout } from '@/hooks/use-sidebar-layout-query'
import { APP_NAME } from '@/lib/constants'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute('/_app/_authed/_sidebar')({
    component: AppLayout,
})

function AppLayout() {
    const { user, logout, hasPermission } = useAuth()
    // Drivers get delivery_trips.read.all (to reach the Delivery Trips menu +
    // API) yet keep shipping_carriers.read (order-form carrier picker). Neither
    // permission can tell "is a driver" apart, so hideForDriver menu entries are
    // resolved by dashboard role instead.
    const dashboardRole = useDashboardRole({ allowKioskRedirect: false })
    const isDriver =
        dashboardRole.kind === 'dashboard' &&
        dashboardRole.dashboard === 'driver'
    const { data: sidebarLayout } = useSidebarLayout()
    // Known flag states for nav filtering. A flagged entry whose key is
    // missing here would be hidden (fail-safe), so register every nav flag.
    const navFlagOn: Record<string, boolean | string> = {}
    const flagAllows = (flag?: string) => !flag || Boolean(navFlagOn[flag])
    const navigate = useNavigate()
    const routerState = useRouterState()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [qrOpen, setQrOpen] = useState(false)

    const currentPath = routerState.location.pathname

    // beforeLoad on _authed guarantees user exists, but useQuery may lag one render cycle
    if (!user) return null

    const handleLogout = async () => {
        await logout()
        void navigate({ to: '/login', search: { redirect: undefined } })
    }

    // Feature-flag + permission gate (both levels, incl. anyPermission via
    // entryAllowsPermission), plus hideForDriver entries for driver accounts.
    // See filterNavEntries.
    const navItems = filterNavEntries(getNavItems(), {
        hasPermission,
        flagAllows,
        isDriver,
    })

    // Dựng cấu trúc theo cấu hình admin (tạo cha, re-parent con, đổi tên) trên
    // tập entries ĐÃ lọc quyền/flag ở trên → giữ nguyên bảo mật (con không được
    // phép xem đã bị loại trước khi dựng). Sau khi dựng, ẩn group rỗng (con đã
    // dời hết đi hoặc user không có quyền). Layout rỗng/chưa load → mặc định.
    const orderedNavItems = buildSidebar(
        navItems,
        sidebarLayout?.layout,
    ).filter((entry) => !('items' in entry) || entry.items.length > 0)

    return (
        // Desktop: khoá shell = chiều cao viewport + ẩn scroll body → header và
        // sidebar đứng yên, chỉ <main> (overflow-auto) và nav sidebar
        // (overflow-y-auto) tự cuộn. Mobile giữ nguyên (min-h-screen, scroll body).
        <div className="min-h-screen flex bg-background lg:h-screen lg:overflow-hidden">
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-20 bg-black/40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 z-30 h-full w-56 bg-card border-r flex flex-col transition-transform duration-200 lg:static lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="h-14 flex items-center justify-center border-b shrink-0">
                    <img
                        src="/logo.svg"
                        alt={APP_NAME}
                        className="h-11 w-auto object-contain"
                    />
                </div>

                {/* Nav links */}
                <AppSidebar
                    entries={orderedNavItems}
                    currentPath={currentPath}
                    onNavigate={() => setSidebarOpen(false)}
                />

                {/* User section */}
                <div className="border-t p-3 shrink-0">
                    <div className="text-xs text-muted-foreground truncate mb-2 px-1">
                        {user.username}
                    </div>
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors w-full"
                    >
                        <LogOut className="size-4 shrink-0" />
                        {m.auth_logout()}
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                <header className="h-14 border-b flex items-center px-4 gap-3 shrink-0 bg-card">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="lg:hidden"
                        onClick={() => setSidebarOpen((o) => !o)}
                    >
                        {sidebarOpen ? (
                            <X className="size-5" />
                        ) : (
                            <Menu className="size-5" />
                        )}
                    </Button>
                    <span className="flex-1 flex items-center gap-2 lg:hidden">
                        <img
                            src="/logo.svg"
                            alt={APP_NAME}
                            className="size-6"
                        />
                    </span>
                    <div className="flex-1 hidden lg:block" />
                    {hasPermission('sales_orders.pickup_order') && (
                        <ActiveTripBadge />
                    )}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => setQrOpen(true)}
                        title={m.nav_scan_qr()}
                    >
                        <QrCode className="size-4" />
                    </Button>
                    <SupportReportTrigger />
                    <WhatsNewTrigger />
                    <NotificationDropdown align="right" />
                    {hasPermission('sales_orders.create') && (
                        <Link
                            to="/orders/quick"
                            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-2.5 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
                            title={m.order_quick_title()}
                        >
                            <Zap className="size-4" />
                            <span className="hidden lg:inline">
                                {m.order_quick_title()}
                            </span>
                        </Link>
                    )}
                    {/* TẠM ẨN nút "Bán hàng" (POS /sale): hiện chưa dùng đến
                        màn POS. Mở lại khi cần bằng cách bỏ comment khối dưới. */}
                    {/* {hasPermission('sales_orders.quick_create') && (
                        <Link
                            to="/sale"
                            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                            title={m.nav_sales()}
                        >
                            <Store className="size-4" />
                            <span className="hidden lg:inline">
                                {m.nav_sales()}
                            </span>
                        </Link>
                    )} */}
                </header>

                <main className="flex-1 overflow-auto p-0 lg:p-6">
                    <Outlet />
                </main>
            </div>

            <QRScannerDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                mode="navigate"
            />
        </div>
    )
}
